import { ThunkAction, ThunkDispatch } from 'redux-thunk';

import * as Model from '../models';
import { deepClone, generateId } from '../models/util';
import { AddOligoRequest, ProjectService } from '../service/project-service';
import { PricingResult, PricingService } from '../service/pricing-service';
import { AppStateUpdateReduxAction, UPDATE_APP_STATE } from './world-actions';

export const ADD_OLIGO = 'ADD_OLIGO';
export const UPDATE_OLIGO = 'UPDATE_OLIGO';
export const DELETE_OLIGO = 'DELETE_OLIGO';

export type OligoAddActionPayload = {
  projectIdx: number;
  oligos: Model.OligoItem[];
};

export type OligoAddFnRequest = {
  projectId: string;
  projectIdx: number;
  nextIdx: number;
  quantity: number;
  preference?: Model.ProjectPreference;
};

export type OligoAddReduxAction = Model.ReduxAction<OligoAddActionPayload>;

export type OligoAddFn = ((request: OligoAddFnRequest) => ThunkAction<ng.IPromise<Model.OligoItem[]>, Model.RootState, object, OligoAddReduxAction | AppStateUpdateReduxAction>);

export type OligoAddDispatch = ThunkDispatch<Model.RootState, object, OligoAddReduxAction | AppStateUpdateReduxAction>;

export interface OligoAddAction {
  addOligo: OligoAddFn;
}

export type OligoAdd = (request: OligoAddFnRequest) => ng.IPromise<Model.OligoItem[] | null>;

export interface OligoAddSupport {
  addOligo: OligoAdd | null;
}

export type OligoUpdateItem = Model.Parameter;

export type OligoUpdateFnRequest = {
  projectIdx: number;
  oligoIdx: number;
  parameterDefId: string;
  parentParameterDefId: string;
  parameters: OligoUpdateItem[];
};

export type OligoUpdateActionPayload = {
  projectIdx: number;
  oligo: Model.OligoItem;
};

export type OligoUpdateReduxAction = Model.ReduxAction<OligoUpdateActionPayload>;

export type OligoUpdateFn = ((request: OligoUpdateFnRequest) => ThunkAction<ng.IPromise<Model.OligoItem>, Model.RootState, object, OligoUpdateReduxAction | AppStateUpdateReduxAction>);

export type OligoUpdateDispatch = ThunkDispatch<Model.RootState, object, OligoUpdateReduxAction | AppStateUpdateReduxAction>;

export interface OligoUpdateAction {
  updateOligo: OligoUpdateFn;
}

export type OligoUpdate = (request: OligoUpdateFnRequest) => ng.IPromise<Model.OligoItem | null>;

export interface OligoUpdateSupport {
  updateOligo: OligoUpdate | null;
}

export type OligoDeleteFnRequest = {
  projectIdx: number;
  id: string;
  index: number;
};

export type OligoUpdateDisplayStateFnRequest = {
  projectIdx: number;
  oligoIdx: number;
  displayType: Model.OligoDisplayType;
  configDisplayType: Model.OligoConfigDisplayType;
};

export type OligoUpdateDisplayStateFn = ((request: OligoUpdateDisplayStateFnRequest) => ThunkAction<ng.IPromise<Model.OligoItem>, Model.RootState, object, OligoUpdateReduxAction>);

export interface OligoUpdateDisplayStateAction {
  updateOligoDisplayState: OligoUpdateDisplayStateFn;
}

export type OligoUpdateDisplayState = (request: OligoUpdateDisplayStateFnRequest) => ng.IPromise<Model.OligoItem | null>;

export interface OligoUpdateDisplayStateSupport {
  updateOligoDisplayState: OligoUpdateDisplayState | null;
}

export type OligoDeleteActionPayload = OligoDeleteFnRequest;

export type OligoDeleteFn = ((request: OligoDeleteFnRequest) => ThunkAction<ng.IPromise<Model.OligoItem>, Model.RootState, object, Model.ReduxAction<OligoDeleteActionPayload>>);

export type OligoDeleteDispatch = ThunkDispatch<Model.RootState, object, Model.ReduxAction<OligoDeleteActionPayload>>;

export interface OligoDeleteAction {
  deleteOligo: OligoDeleteFn;
}

export type OligoDelete = (request: OligoDeleteFnRequest) => ng.IPromise<Model.OligoItem | null>;

export interface OligoDeleteSupport {
  deleteOligo: OligoDelete | null;
}

export interface OligoActions extends OligoUpdateAction, OligoAddAction, OligoDeleteAction, OligoUpdateDisplayStateAction {
}

function namesParamDefUpdater($q: ng.IQService,
                              projectService: ProjectService):
  (updatedParamDefId: string, updatedParam: Model.OligoParam, oligoItem: Model.OligoItem) => ng.IPromise<Model.OligoItem> {
  return (updatedParamDefId: string, updatedParam: Model.OligoParam, oligoItem: Model.OligoItem) => {
    const deferred: ng.IDeferred<Model.OligoItem> = $q.defer();
    const oligoParamDefs: Model.OligoParamDefs = oligoItem.definition;
    const { oligoName: origOligoName, researcherName: origResearcherName } = oligoParamDefs.basic;
    projectService.oligoNamesDefinition(updatedParam).then((updatedParamDefs: Model.OligoParamDefs) => {
      const { status: previousStatus } = oligoParamDefs;
      const { violatingParamIds: prevViolatingIds } = previousStatus;
      const { status: newStatus } = updatedParamDefs;
      const { violatingParamIds: newViolatingIds } = newStatus;
      const { oligoName, researcherName } = updatedParamDefs.basic;
      const prevViolationsNotNameRelated: string[] = prevViolatingIds.filter((paramDefId: string) =>
        paramDefId !== researcherName.id && paramDefId !== oligoName.id);
      const remainingViolatingIds: string[] = [...prevViolationsNotNameRelated, ...newViolatingIds];
      const state: Model.OligoState = remainingViolatingIds.length > 0 ? Model.OligoStates.INVALID : Model.OligoStates.VALID;
      updatedParamDefs = {
        ...oligoParamDefs,
        ...{
          basic: {
            ...oligoParamDefs.basic,
            oligoName: {
              ...oligoName,
              ...{
                pristine: origOligoName.pristine &&
                          (updatedParamDefId !== oligoName.id),
              },
            },
            researcherName: {
              ...researcherName,
              ...{
                pristine: origResearcherName.pristine &&
                          (updatedParamDefId !== researcherName.id),
              },
            },
          },
        },
        ...{
          status: {
            state,
            violatingParamIds: remainingViolatingIds,
          },
        },
      };
      const { id, index, displayType, configDisplayType } = oligoItem;
      const oligo: Model.OligoItem = {
        id,
        index,
        displayType,
        configDisplayType,
        pricing: oligoItem.pricing,
        definition: updatedParamDefs,
        parameter: updatedParam,
      };
      deferred.resolve(oligo);
    }, deferred.reject);
    return deferred.promise;
  };
}

function paramDefUpdater($q: ng.IQService,
                         projectService: ProjectService,
                         pricingService: PricingService):
  (dispatch: OligoUpdateDispatch, projectIdx: number, updatedParam: Model.OligoParam, oligoItem: Model.OligoItem) => ng.IPromise<Model.OligoItem> {
  return (dispatch: OligoUpdateDispatch, projectIdx: number, updatedParam: Model.OligoParam, oligoItem: Model.OligoItem) => {
    const deferred: ng.IDeferred<Model.OligoItem> = $q.defer();
    projectService.oligoParameterDefinition(updatedParam).then((updatedParamDefs: Model.OligoParamDefs) => {
      const { id, index, displayType, configDisplayType } = oligoItem;
      const oligo: Model.OligoItem = {
        id,
        index,
        displayType,
        configDisplayType,
        parameter: updatedParam,
        definition: updatedParamDefs,
      };
      const { basic, properties } = updatedParamDefs;
      const { pricing: pricingProps } = properties;
      const { supported: pricingSupported } = pricingProps;
      const oligoValueDefined: boolean = basic.oligoValue.value ?  basic.oligoValue.value.length > 0 : false;
      if (pricingSupported && oligoValueDefined) {
        oligo.pricing = {
          amount: 0,
          available: pricingSupported,
          currency: '',
          loading: true,
        };
        dispatch({ type: UPDATE_OLIGO, payload: { projectIdx, oligo } });
        pricingService.priceForOligo(oligo).then((pricing: PricingResult) => {
          const oligoWithPrice: Model.OligoItem = { ...oligo, ...{ pricing } };
          deferred.resolve(oligoWithPrice);
        }, deferred.reject);
      } else {
        deferred.resolve(oligo);
      }
    }, deferred.reject);
    return deferred.promise;
  };
}

function useAsTemplate(template: Model.OligoItem | null): (oligo: Model.OligoItem) => Model.OligoItem {
  return (oligo: Model.OligoItem) => {
    if (!template) {
      return oligo;
    }
    try {
      // currently, we're just using the researcher name from the template oligo
      const { researcherName } = template.definition.basic;
      if (researcherName.invalid) {
        return oligo;
      }
      const { researcherName: researcherNameParam } = template.parameter.basic;
      oligo.definition.basic.researcherName = deepClone(researcherName);
      oligo.parameter.basic.researcherName = deepClone(researcherNameParam);
    } catch (e) {
      console.error(e);
    }
    return oligo;
  };
}

export type OligoActionsService = ($q: ng.IQService, projectService: ProjectService, pricingService: PricingService) => OligoActions;
export const OLIGO_ACTIONS_SERVICE_NAME = 'OligoActionsService';
export const OligoActionsService: OligoActionsService = ($q: ng.IQService, projectService: ProjectService, pricingService: PricingService): OligoActions => {
  const updateParamDefs = paramDefUpdater($q, projectService, pricingService);
  const updateNameParamDefs = namesParamDefUpdater($q, projectService);
  return {
    addOligo: (request: OligoAddFnRequest) => {
      return (dispatch: OligoAddDispatch,
              getState: () => Model.RootState,
              extraArgs: object) => {
        const deferred: ng.IDeferred<Model.OligoItem[]> = $q.defer();
        const { nextIdx, projectIdx, projectId, preference, quantity } = request;
        const newOligoRequests = Array.from(Array(quantity || 1).keys()).map((key: number) => {
          const index = nextIdx + key;
          const id: string = `${generateId()}_${index}_${projectId}`;
          const addRequest: AddOligoRequest = { index, preference, id };
          return projectService.newOligoItem(addRequest);
        });
        dispatch({ type: UPDATE_APP_STATE, payload: Model.AppStates.UPDATE });
        $q.all(newOligoRequests).then((oligos: Model.OligoItem[]) => {
          const prevIdx: number = nextIdx - 1;
          const project: Model.ProjectItem = getState().projects[projectIdx];
          const prevOligo: Model.OligoItem | null = (project.oligos || [])[prevIdx];
          oligos = oligos.map(useAsTemplate(prevOligo));
          dispatch({ type: ADD_OLIGO, payload: { projectIdx, oligos } });
          dispatch({ type: UPDATE_APP_STATE, payload: Model.AppStates.DRAFT });
          deferred.resolve(oligos);
        }, deferred.reject);
        return deferred.promise;
      };
    },
    updateOligo: (request: OligoUpdateFnRequest) => {
      return (dispatch: OligoUpdateDispatch,
              getState: () => Model.RootState,
              extraArgs: object): ng.IPromise<Model.OligoItem> => {
        const deferred: ng.IDeferred<Model.OligoItem> = $q.defer();
        const { projectIdx, oligoIdx, parentParameterDefId, parameterDefId, parameters } = request;
        const oligoItem: Model.OligoItem = getState().projects[projectIdx].oligos[oligoIdx];
        const origParam: Model.OligoParam = oligoItem.parameter;
        const updatedParam: Model.OligoParam = {
          basic: { ...origParam.basic },
          advanced: { ...origParam.advanced },
        };
        const oligoParamDefs: Model.OligoParamDefs = oligoItem.definition;
        const paramDef: Model.ClientParamDef<object> = oligoParamDefs[parentParameterDefId][parameterDefId] as Model.ClientParamDef<object>;
        const selectedObj: object = (paramDef as Model.SelectionTypeClientParamDef<object, object>).selected;
        if (Array.isArray(selectedObj)) {
          updatedParam[parentParameterDefId][parameterDefId] = parameters;
        } else {
          updatedParam[parentParameterDefId][parameterDefId] = parameters[0];
        }
        const hasNoChange: boolean = JSON.stringify(origParam) === JSON.stringify(updatedParam);
        if (hasNoChange) {
          deferred.resolve();
          return deferred.promise;
        }
        const retainCurrentState: (error: object) => void = (error: object) => {
          dispatch({ type: UPDATE_OLIGO, payload: { projectIdx, oligo: { ...oligoItem } } });
          deferred.reject(error);
        };
        const { basic } = oligoParamDefs;
        const { oligoName, researcherName } = basic;
        const isOligoNameUpdate: boolean = parameterDefId === oligoName.id;
        const isResearcherNameUpdate: boolean = parameterDefId === researcherName.id;
        const isNameParamDefsUpdate: boolean = isOligoNameUpdate || isResearcherNameUpdate;
        dispatch({ type: UPDATE_APP_STATE, payload: Model.AppStates.UPDATE });
        if (isNameParamDefsUpdate) {
          updateNameParamDefs(parameterDefId, updatedParam, oligoItem)
            .then((oligo: Model.OligoItem) => {
              dispatch({ type: UPDATE_OLIGO, payload: { projectIdx, oligo } });
              dispatch({ type: UPDATE_APP_STATE, payload: Model.AppStates.DRAFT });
              deferred.resolve(oligo);
            }, retainCurrentState);
        } else {
          updateParamDefs(dispatch, projectIdx, updatedParam, oligoItem)
            .then((oligo: Model.OligoItem) => {
              dispatch({ type: UPDATE_OLIGO, payload: { projectIdx, oligo } });
              dispatch({ type: UPDATE_APP_STATE, payload: Model.AppStates.DRAFT });
              deferred.resolve(oligo);
            }, retainCurrentState);
        }
        return deferred.promise;
      };
    },
    deleteOligo: (request: OligoDeleteFnRequest) => {
      return (dispatch: OligoDeleteDispatch,
              getState: () => Model.RootState,
              extraArgs: object): ng.IPromise<Model.OligoItem> => {
        const deferred: ng.IDeferred<Model.OligoItem> = $q.defer();
        const { projectIdx, index: oligoIdx } = request;
        const oligoItem: Model.OligoItem = getState().projects[projectIdx].oligos[oligoIdx];
        dispatch({ type: DELETE_OLIGO, payload: request });
        deferred.resolve(oligoItem);
        return deferred.promise;
      };
    },
    updateOligoDisplayState: (request: OligoUpdateDisplayStateFnRequest) => {
      return (dispatch: OligoUpdateDispatch,
              getState: () => Model.RootState,
              extraArgs: object): ng.IPromise<Model.OligoItem> => {
        const deferred: ng.IDeferred<Model.OligoItem> = $q.defer();
        const { projectIdx, oligoIdx, displayType, configDisplayType } = request;
        const oligoItem: Model.OligoItem = getState().projects[projectIdx].oligos[oligoIdx];
        const oligo: Model.OligoItem = {
          ...oligoItem,
          displayType,
          configDisplayType,
        };
        dispatch({ type: UPDATE_OLIGO, payload: { projectIdx, oligo } });
        return deferred.promise;
      };
    },
  };
};



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0-0!./src/main/script/actions/oligo-actions.ts